import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types (mirror FinalInspectionDetail from web) ─────────────────────────
interface ChecklistItem {
  id: string;
  title: string;
  category?: string | null;
  status?: 'pass' | 'fail' | 'na' | null;
  remarks?: string | null;
  evidence_images?: string[];
}

interface DefectItem {
  id: string;
  sku_id?: string | null;
  type?: string | null;
  title?: string | null;
  category?: string | null;
  severity?: string | null;
  level?: string | null;
  count?: number;
  location?: string | null;
  description?: string | null;
  evidence_images?: string[];
}

interface PhotoItem {
  id: string;
  sku_id?: string | null;
  tile_key?: string | null;
  label?: string | null;
  tile_label?: string | null;
  image_url: string;
  is_mandatory?: boolean;
  required?: boolean;
}

interface AuditItem {
  id: string;
  sku_id?: string | null;
  type: 'MEASUREMENT' | 'WEIGHT' | 'METAL_DETECTION';
  data?: any;
  result?: 'pass' | 'fail' | 'pending';
  notes?: string | null;
  remarks?: string | null;
}

interface SkuItem {
  id: string;
  po_sku_id?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  description?: string | null;
  sku_description?: string | null;
  ordered_qty?: number;
  quantity?: number;
  inspected_qty?: number;
  result?: 'pass' | 'fail' | 'pending';
  colour?: string | null;
  color?: string | null;
  size?: string | null;
  vendor_style?: string | null;
  vendorStyle?: string | null;
  production_completion?: number;
  productionCompletion?: number;
  metal_detection?: string | null;
  metalDetection?: string | null;
  defects?: DefectItem[];
  photoTiles?: PhotoItem[];
  photo_tiles?: PhotoItem[];
  photos?: PhotoItem[];
}

export interface FinalInspectionPDFData {
  id: string;
  po_number?: string | null;
  customer_name?: string | null;
  product_category?: string | null;
  po_ship_date?: string | null;
  po_notes?: string | null;
  inspection_level?: string | null;
  aql_major?: string | null;
  aql_minor?: string | null;
  aql_critical?: string | null;
  lot_size?: number;
  remarks?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  overall_result: 'PASS' | 'FAIL' | 'PENDING';
  inspector_name?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  created_at: string;
  inspector?: { id: string; name: string; profileImage?: string | null };
  checklists?: ChecklistItem[];
  defects?: DefectItem[];
  skus?: SkuItem[];
  photos?: PhotoItem[];
  audits?: AuditItem[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getSkuSampleSize = (qty: number) => {
  if (qty <= 8) return 2;
  if (qty <= 15) return 3;
  if (qty <= 25) return 5;
  if (qty <= 50) return 8;
  if (qty <= 90) return 13;
  if (qty <= 150) return 20;
  if (qty <= 280) return 32;
  if (qty <= 500) return 50;
  if (qty <= 1200) return 80;
  if (qty <= 3200) return 125;
  if (qty <= 10000) return 200;
  if (qty <= 35000) return 315;
  if (qty <= 150000) return 500;
  return 800;
};

const getAuditImages = (auditData: any): string[] => {
  if (!auditData) return [];
  const arr = Array.isArray(auditData.imageUrls) ? auditData.imageUrls
    : Array.isArray(auditData.image_urls) ? auditData.image_urls
    : Array.isArray(auditData.evidence_images) ? auditData.evidence_images
    : [];
  return (arr as string[]).filter(Boolean);
};

const getAuditDescs = (auditData: any): string[] => {
  if (!auditData) return [];
  return (Array.isArray(auditData.imageDescriptions) ? auditData.imageDescriptions
    : Array.isArray(auditData.image_descriptions) ? auditData.image_descriptions
    : []) as string[];
};

// ─── Styles ─────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  // Fixed page header
  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#000000' },
  titleLine2: { fontSize: 9, color: '#555555' },

  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },
  hrThin: { borderBottomWidth: 0.5, borderBottomColor: '#cccccc', marginVertical: 6 },

  // Result summary row
  resultRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000000', marginBottom: 12 },
  resultBox: { paddingHorizontal: 12, paddingVertical: 8, borderRightWidth: 1, borderRightColor: '#aaaaaa', justifyContent: 'center', minWidth: 110 },
  resultLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2 },
  resultValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#000000' },
  statBox: { flex: 1, paddingVertical: 8, paddingHorizontal: 6, borderRightWidth: 0.5, borderRightColor: '#cccccc', alignItems: 'center', justifyContent: 'center' },
  statLabel: { fontSize: 7.5, color: '#666666', textAlign: 'center', marginBottom: 2 },
  statValue: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },
  statValueSm: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },

  // Two-column info block
  twoCol: { flexDirection: 'row', gap: 20, marginBottom: 10 },
  col: { flex: 1 },
  infoRow: { flexDirection: 'row', marginBottom: 4 },
  infoLabel: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', width: 100 },
  infoValue: { fontSize: 8.5, flex: 1 },
  aqlSubLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#555555', marginTop: 6, marginBottom: 4, textTransform: 'uppercase' },

  // Gray section bar (major sections)
  sectionHeader: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#000000' },

  // Lighter sub-bar (within SKU sections)
  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  // SKU summary table
  tableHead: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#000000', paddingBottom: 3, marginBottom: 2 },
  th: { fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1, paddingRight: 3 },
  thNarrow: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 52, textAlign: 'center' },
  thStatus: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 46, textAlign: 'right' },
  tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#dddddd', paddingVertical: 3 },
  td: { fontSize: 8.5, flex: 1, paddingRight: 3 },
  tdBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', flex: 1, paddingRight: 3 },
  tdNarrow: { fontSize: 8.5, width: 52, textAlign: 'center' },
  tdStatus: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 46, textAlign: 'right' },

  // SKU detail block
  skuBar: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 6, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  skuBarText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#000000' },
  skuBadge: { fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 2, borderWidth: 0.5, borderColor: '#555555' },
  skuMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, paddingHorizontal: 4, marginBottom: 6 },
  skuMetaItem: { flexDirection: 'row', gap: 3 },
  skuMetaLabel: { fontSize: 8, color: '#666666' },
  skuMetaValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },

  // Production completion
  progressLabel: { fontSize: 8, color: '#444444', paddingHorizontal: 4, marginBottom: 3 },
  progressTrack: { height: 5, backgroundColor: '#e2e8f0', marginHorizontal: 4, marginBottom: 8 },
  progressFill: { height: 5, backgroundColor: '#555555' },

  // Photo tiles (3-column layout at 158pt wide each)
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  photoTile: { width: 158 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 158, height: 118, objectFit: 'cover' },

  // Audit
  auditMetaRow: { flexDirection: 'row', gap: 24, paddingHorizontal: 4, marginBottom: 4 },
  auditMetaItem: { flexDirection: 'row', gap: 4 },
  auditMetaLabel: { fontSize: 8.5, color: '#666666' },
  auditMetaValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  auditNotes: { fontSize: 8, color: '#444444', fontStyle: 'italic', paddingHorizontal: 4, marginBottom: 4 },
  auditImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  auditImgTile: { width: 120 },
  auditImg: { width: 120, height: 90, objectFit: 'cover' },
  auditImgCaption: { fontSize: 7, color: '#666666', marginTop: 2 },

  // Metal detection
  metalRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 4, marginBottom: 6, marginTop: 4 },
  metalBadge: { fontSize: 9, fontFamily: 'Helvetica-Bold', paddingHorizontal: 8, paddingVertical: 3, borderWidth: 0.5, borderColor: '#666666' },
  metalNotes: { fontSize: 8, color: '#444444', flex: 1 },

  // Defects
  defectBox: { marginHorizontal: 4, marginBottom: 6, paddingBottom: 6, borderBottomWidth: 0.5, borderBottomColor: '#eeeeee' },
  defectTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
  defectMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 3 },
  defectMetaItem: { flexDirection: 'row', gap: 3 },
  defectMetaLabel: { fontSize: 8, color: '#666666' },
  defectMetaValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
  defectDesc: { fontSize: 8, color: '#444444', fontStyle: 'italic', marginBottom: 4 },
  defectImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  defectImg: { width: 120, height: 90, objectFit: 'cover' },

  // No-data note
  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  // Checklist
  checkCategory: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginTop: 6, marginBottom: 3, paddingHorizontal: 4 },
  checkRow: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 0.5, borderBottomColor: '#eeeeee', paddingVertical: 4, paddingHorizontal: 6 },
  checkTitle: { fontSize: 8.5, flex: 1, marginRight: 8 },
  checkValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', width: 90, textAlign: 'right' },
  checkRemarks: { fontSize: 8, color: '#444444', fontStyle: 'italic', paddingHorizontal: 6, paddingBottom: 3 },
  checkImgRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingHorizontal: 6, paddingBottom: 6 },
  checkImg: { width: 80, height: 60, objectFit: 'cover' },

  // SKU divider
  skuDivider: { borderBottomWidth: 1, borderBottomColor: '#aaaaaa', marginVertical: 12 },

  // Remarks
  remarksText: { fontSize: 9, paddingHorizontal: 4, paddingVertical: 6, minHeight: 40 },

  // Signatures
  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },

  // Fixed footer
  pageFooter: {
    position: 'absolute',
    bottom: 14,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ───────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Text style={s.logoText}>QA MATE</Text>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>FINAL INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft} />
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionHeaderText}>{title}</Text>
  </View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

// ─── Main PDF Document ───────────────────────────────────────────────────────
const FinalInspectionReportPDF = ({ inspection }: { inspection: FinalInspectionPDFData }) => {
  const result = inspection.overall_result || 'PENDING';
  const inspectorName = inspection.inspector?.name || inspection.inspector_name || 'N/A';
  const skus = inspection.skus || [];
  const globalAudits = inspection.audits || [];
  const globalDefects = inspection.defects || [];
  const globalPhotos = inspection.photos || [];
  const checklists = inspection.checklists || [];
  const reportId = `QFI ${inspection.id.slice(0, 6).toUpperCase()}`;

  const majorCount = globalDefects
    .filter(d => (d.level || d.severity || '').toLowerCase() === 'major')
    .reduce((acc, d) => acc + (d.count || 1), 0);
  const minorCount = globalDefects
    .filter(d => (d.level || d.severity || '').toLowerCase() === 'minor')
    .reduce((acc, d) => acc + (d.count || 1), 0);

  const checklistByCategory = (() => {
    const groups = new Map<string, ChecklistItem[]>();
    checklists.forEach((c) => {
      const key = c.category || 'General';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    });
    return Array.from(groups.entries());
  })();

  // Mirrors the web's photo-association logic exactly
  const getSkuPhotos = (sku: SkuItem) => {
    const skuId = sku.po_sku_id || sku.id;

    const matched = skuId
      ? globalPhotos.filter((p) => p.sku_id === skuId)
      : globalPhotos.length === 0
        ? [...(sku.photoTiles || sku.photo_tiles || sku.photos || [])]
        : [];

    const withUrl = matched.filter((p) => p.image_url && p.image_url.length > 0);
    return {
      mandatory: withUrl.filter((p) => p.is_mandatory === true || p.required === true),
      optional: withUrl.filter((p) => !p.is_mandatory && !p.required),
    };
  };

  return (
    <Document title={`Final Inspection Report - ${inspection.po_number || inspection.id}`}>

      {/* ═══ PAGE 1 — Result summary + Order Info + AQL + SKU Summary ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        {/* Result summary row: mirrors the web's result banner stats */}
        <View style={s.resultRow}>
          <View style={s.resultBox}>
            <Text style={s.resultLabel}>INSPECTION RESULT</Text>
            <Text style={s.resultValue}>{result}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Major Defects</Text>
            <Text style={s.statValue}>{String(majorCount).padStart(2, '0')}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Minor Defects</Text>
            <Text style={s.statValue}>{String(minorCount).padStart(2, '0')}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Lot Size</Text>
            <Text style={s.statValue}>{inspection.lot_size || 0}</Text>
          </View>
          <View style={[s.statBox, { borderRightWidth: 0 }]}>
            <Text style={s.statLabel}>Report ID</Text>
            <Text style={s.statValueSm}>{reportId}</Text>
          </View>
        </View>

        {/* Two-column: Order Information | Inspector & AQL */}
        <View style={s.twoCol}>
          <View style={s.col}>
            <SectionBar title="ORDER INFORMATION" />
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Customer :</Text>
              <Text style={s.infoValue}>{inspection.customer_name || 'N/A'}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>PO Number :</Text>
              <Text style={s.infoValue}>{inspection.po_number || 'N/A'}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Inspection Date :</Text>
              <Text style={s.infoValue}>{formatDate(inspection.created_at)}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Ship Date :</Text>
              <Text style={s.infoValue}>{formatDate(inspection.po_ship_date)}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Item Description :</Text>
              <Text style={s.infoValue}>{inspection.product_category || inspection.po_notes || 'N/A'}</Text>
            </View>
          </View>

          <View style={s.col}>
            <SectionBar title="INSPECTOR &amp; AQL" />
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Inspector :</Text>
              <Text style={s.infoValue}>{inspectorName}</Text>
            </View>
            <Text style={s.aqlSubLabel}>AQL Sampling Summary</Text>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Insp Level :</Text>
              <Text style={s.infoValue}>{inspection.inspection_level || 'N/A'}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Major AQL :</Text>
              <Text style={s.infoValue}>{inspection.aql_major || 'N/A'}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Minor AQL :</Text>
              <Text style={s.infoValue}>{inspection.aql_minor || 'N/A'}</Text>
            </View>
            {inspection.aql_critical ? (
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Critical AQL :</Text>
                <Text style={s.infoValue}>{inspection.aql_critical}</Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* SKU Summary table — matches web column layout exactly */}
        {skus.length > 0 && (
          <>
            <SectionBar title="SKU SUMMARY" />
            <View style={s.tableHead}>
              <Text style={[s.th, { flex: 1.8 }]}>SKU / Style</Text>
              <Text style={[s.th, { flex: 0.8 }]}>Vendor Code</Text>
              <Text style={s.th}>Description</Text>
              <Text style={s.thNarrow}>Order Qty</Text>
              <Text style={s.thNarrow}>Bundles</Text>
              <Text style={s.thNarrow}>Insp Qty</Text>
              <Text style={s.thNarrow}>Sample</Text>
              <Text style={s.thStatus}>Status</Text>
            </View>
            {skus.map((sku) => {
              const qty = sku.quantity ?? sku.ordered_qty ?? 0;
              const colour = sku.colour || sku.color || '';
              const size = sku.size || '';
              const variant = [colour, size ? `S:${size}` : ''].filter(Boolean).join(' | ');
              const statusStr = (sku.result || 'PENDING').toUpperCase();
              return (
                <View key={sku.id} style={s.tableRow}>
                  <View style={{ flex: 1.8, paddingRight: 3, flexDirection: 'column' }}>
                    <Text style={{ fontSize: 8.5, fontFamily: 'Helvetica-Bold' }}>{sku.sku_code || sku.sku_name || 'N/A'}</Text>
                    {variant ? <Text style={{ fontSize: 7, color: '#666666', marginTop: 1 }}>{variant}</Text> : null}
                  </View>
                  <Text style={[s.td, { flex: 0.8 }]}>{sku.vendorStyle || sku.vendor_style || '—'}</Text>
                  <View style={{ flex: 1, paddingRight: 3 }}>
                    <Text style={s.td}>{sku.description || sku.sku_description || '—'}</Text>
                  </View>
                  <Text style={s.tdNarrow}>{qty}</Text>
                  <Text style={s.tdNarrow}>{Math.ceil(qty / 5)}</Text>
                  <Text style={s.tdNarrow}>{sku.inspected_qty || qty}</Text>
                  <Text style={s.tdNarrow}>{getSkuSampleSize(qty)}</Text>
                  <Text style={s.tdStatus}>{statusStr}</Text>
                </View>
              );
            })}
          </>
        )}

        <PageFooter />
      </Page>

      {/* ═══ CHECKLIST PAGE ═══ */}
      {checklistByCategory.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="CHECKLIST &amp; VERIFICATIONS" />
          {checklistByCategory.map(([category, items]) => (
            <View key={category}>
              <Text style={s.checkCategory}>{category}</Text>
              {items.map((item) => {
                const st = (item.status || 'na').toLowerCase();
                const label = st === 'pass' ? 'YES' : st === 'fail' ? 'NO' : 'NOT APPLICABLE';
                const hasRemarks = !!(item.remarks && item.remarks.trim().length > 0);
                const hasImages = !!(item.evidence_images && item.evidence_images.length > 0);
                return (
                  <View key={item.id}>
                    <View style={s.checkRow}>
                      <Text style={s.checkTitle}>{item.title}</Text>
                      <Text style={s.checkValue}>{label}</Text>
                    </View>
                    {hasRemarks && (
                      <Text style={s.checkRemarks}>Remarks: {item.remarks}</Text>
                    )}
                    {hasImages && (
                      <View style={s.checkImgRow}>
                        {item.evidence_images!.filter(src => src).map((src, i) => (
                          <Image key={i} src={src} style={s.checkImg} />
                        ))}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
          <PageFooter />
        </Page>
      )}

      {/* ═══ DETAILED SKU REPORT — all SKU data nested under each SKU ═══ */}
      {skus.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="DETAILED SKU REPORT" />

          {skus.map((sku, skuIdx) => {
            const skuId = sku.po_sku_id || sku.id;
            const skuName = sku.sku_name || sku.sku_code || '—';
            const colour = sku.colour || sku.color || '';
            const size = sku.size || '';
            const vendorStyle = sku.vendorStyle || sku.vendor_style || '';
            const qty = sku.quantity ?? sku.ordered_qty ?? 0;
            const completion = (sku.productionCompletion ?? sku.production_completion ?? 0) as number;
            const completionPct = Math.min(Math.max(completion, 0), 100);
            const skuResult = (sku.result || '').toUpperCase();

            const skuAudits = skuId ? globalAudits.filter(a => a.sku_id === skuId) : [];
            const measAudit = skuAudits.find(a => a.type === 'MEASUREMENT');
            const weightAudit = skuAudits.find(a => a.type === 'WEIGHT');
            const metalAudit = skuAudits.find(a => a.type === 'METAL_DETECTION');

            let metalDetection = sku.metal_detection || sku.metalDetection || '';
            if (!metalDetection && metalAudit)
              metalDetection = (metalAudit.data?.result || metalAudit.result || '') as string;

            const skuDefects = skuId
              ? globalDefects.filter(d => d.sku_id === skuId)
              : globalDefects.length === 0 ? [...(sku.defects || [])] : [];

            const { mandatory: mandatoryPhotos, optional: optionalPhotos } = getSkuPhotos(sku);

            return (
              <View key={sku.id}>
                {/* SKU gray bar with name + result badge */}
                <View style={s.skuBar}>
                  <Text style={s.skuBarText}>
                    {skuName}{colour ? ` (${colour})` : ''}
                  </Text>
                  {skuResult ? <Text style={s.skuBadge}>{skuResult}</Text> : null}
                </View>

                {/* SKU meta row */}
                <View style={s.skuMeta}>
                  {vendorStyle ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Vendor:</Text>
                      <Text style={s.skuMetaValue}>{vendorStyle}</Text>
                    </View>
                  ) : null}
                  {size ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Size:</Text>
                      <Text style={s.skuMetaValue}>{size}</Text>
                    </View>
                  ) : null}
                  {colour ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Colour:</Text>
                      <Text style={s.skuMetaValue}>{colour}</Text>
                    </View>
                  ) : null}
                  <View style={s.skuMetaItem}>
                    <Text style={s.skuMetaLabel}>Order Qty:</Text>
                    <Text style={s.skuMetaValue}>{qty}</Text>
                  </View>
                  <View style={s.skuMetaItem}>
                    <Text style={s.skuMetaLabel}>Inspected Qty:</Text>
                    <Text style={s.skuMetaValue}>{sku.inspected_qty || qty}</Text>
                  </View>
                </View>

                {/* Production completion (only if value > 0) */}
                {completionPct > 0 && (
                  <>
                    <Text style={s.progressLabel}>Production Completion: {completionPct.toFixed(0)}%</Text>
                    <View style={s.progressTrack}>
                      <View style={[s.progressFill, { width: `${completionPct}%` }]} />
                    </View>
                  </>
                )}

                {/* Mandatory photos */}
                {mandatoryPhotos.length > 0 && (
                  <>
                    <SubBar title="MANDATORY IMAGES" />
                    <View style={s.photoGrid}>
                      {mandatoryPhotos.map((photo, i) => (
                        <View key={photo.id || i} style={s.photoTile}>
                          <Text style={s.photoLabel}>
                            {photo.label || photo.tile_label || photo.tile_key || 'Photo'}
                          </Text>
                          <Image src={photo.image_url} style={s.photoImg} />
                        </View>
                      ))}
                    </View>
                  </>
                )}

                {/* Optional photos */}
                {optionalPhotos.length > 0 && (
                  <>
                    <SubBar title="OPTIONAL IMAGES" />
                    <View style={s.photoGrid}>
                      {optionalPhotos.map((photo, i) => (
                        <View key={photo.id || i} style={s.photoTile}>
                          <Text style={s.photoLabel}>
                            {photo.label || photo.tile_label || photo.tile_key || 'Photo'}
                          </Text>
                          <Image src={photo.image_url} style={s.photoImg} />
                        </View>
                      ))}
                    </View>
                  </>
                )}

                {/* Measurement Audit */}
                {measAudit ? (
                  <>
                    <SubBar title="MEASUREMENT AUDIT" />
                    {(() => {
                      const ad = measAudit.data || {};
                      const sampleSize = ad.sampleSize ?? ad.sample_size;
                      const auditResult = (measAudit.result || '').toUpperCase();
                      const notes = measAudit.notes || measAudit.remarks || ad.notes || ad.remarks || '';
                      const imgs = getAuditImages(ad);
                      const descs = getAuditDescs(ad);
                      return (
                        <>
                          <View style={s.auditMetaRow}>
                            {sampleSize !== undefined ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Sample Size:</Text>
                                <Text style={s.auditMetaValue}>{sampleSize}</Text>
                              </View>
                            ) : null}
                            <View style={s.auditMetaItem}>
                              <Text style={s.auditMetaLabel}>Status:</Text>
                              <Text style={s.auditMetaValue}>{auditResult || 'N/A'}</Text>
                            </View>
                          </View>
                          {imgs.length > 0 && (
                            <View style={s.auditImgRow}>
                              {imgs.map((src, i) => (
                                <View key={i} style={s.auditImgTile}>
                                  <Image src={src} style={s.auditImg} />
                                  {descs[i] ? <Text style={s.auditImgCaption}>{descs[i]}</Text> : null}
                                </View>
                              ))}
                            </View>
                          )}
                          {notes ? <Text style={s.auditNotes}>{notes}</Text> : null}
                        </>
                      );
                    })()}
                  </>
                ) : null}

                {/* Weight Audit */}
                {weightAudit ? (
                  <>
                    <SubBar title="WEIGHT AUDIT" />
                    {(() => {
                      const ad = weightAudit.data || {};
                      const sampleSize = ad.sampleSize ?? ad.sample_size;
                      const auditResult = (weightAudit.result || '').toUpperCase();
                      const notes = weightAudit.notes || weightAudit.remarks || ad.notes || ad.remarks || '';
                      const imgs = getAuditImages(ad);
                      const descs = getAuditDescs(ad);
                      return (
                        <>
                          <View style={s.auditMetaRow}>
                            {sampleSize !== undefined ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Sample Size:</Text>
                                <Text style={s.auditMetaValue}>{sampleSize}</Text>
                              </View>
                            ) : null}
                            <View style={s.auditMetaItem}>
                              <Text style={s.auditMetaLabel}>Status:</Text>
                              <Text style={s.auditMetaValue}>{auditResult || 'N/A'}</Text>
                            </View>
                          </View>
                          {imgs.length > 0 && (
                            <View style={s.auditImgRow}>
                              {imgs.map((src, i) => (
                                <View key={i} style={s.auditImgTile}>
                                  <Image src={src} style={s.auditImg} />
                                  {descs[i] ? <Text style={s.auditImgCaption}>{descs[i]}</Text> : null}
                                </View>
                              ))}
                            </View>
                          )}
                          {notes ? <Text style={s.auditNotes}>{notes}</Text> : null}
                        </>
                      );
                    })()}
                  </>
                ) : null}

                {/* Metal Detection */}
                {(metalDetection || metalAudit) ? (
                  <>
                    <SubBar title="METAL DETECTION" />
                    <View style={s.metalRow}>
                      {metalDetection ? (
                        <Text style={s.metalBadge}>{metalDetection.toUpperCase()}</Text>
                      ) : null}
                      {metalAudit?.notes ? (
                        <Text style={s.metalNotes}>{metalAudit.notes}</Text>
                      ) : null}
                    </View>
                  </>
                ) : null}

                {/* Defect Findings */}
                {skuDefects.length > 0 ? (
                  <>
                    <SubBar title="DEFECT FINDINGS" />
                    {skuDefects.map((defect) => {
                      const level = (defect.level || defect.severity || '').toUpperCase();
                      const evidenceImgs = (defect.evidence_images || []).filter(src => src && src.length > 0);
                      return (
                        <View key={defect.id} style={s.defectBox}>
                          <Text style={s.defectTitle}>{defect.title || defect.type || 'Defect'}</Text>
                          <View style={s.defectMeta}>
                            {defect.category ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Category:</Text>
                                <Text style={s.defectMetaValue}>{defect.category}</Text>
                              </View>
                            ) : null}
                            {defect.type ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Type:</Text>
                                <Text style={s.defectMetaValue}>{defect.type}</Text>
                              </View>
                            ) : null}
                            {level ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Severity:</Text>
                                <Text style={s.defectMetaValue}>{level}</Text>
                              </View>
                            ) : null}
                            <View style={s.defectMetaItem}>
                              <Text style={s.defectMetaLabel}>Count:</Text>
                              <Text style={s.defectMetaValue}>{defect.count || 1}</Text>
                            </View>
                            {defect.location ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Location:</Text>
                                <Text style={s.defectMetaValue}>{defect.location}</Text>
                              </View>
                            ) : null}
                          </View>
                          {defect.description ? (
                            <Text style={s.defectDesc}>"{defect.description}"</Text>
                          ) : null}
                          {evidenceImgs.length > 0 && (
                            <View style={s.defectImgRow}>
                              {evidenceImgs.map((src, i) => (
                                <Image key={i} src={src} style={s.defectImg} />
                              ))}
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </>
                ) : (
                  <Text style={s.noDataText}>The SKU meets all quality standards and has no defects.</Text>
                )}

                {skuIdx < skus.length - 1 && <View style={s.skuDivider} />}
              </View>
            );
          })}

          <PageFooter />
        </Page>
      )}

      {/* ═══ FINAL PAGE — Remarks + Signatures ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        <SectionBar title="FINAL REMARKS" />
        <Text style={s.remarksText}>{inspection.remarks || '—'}</Text>

        <View style={{ marginTop: 16 }}>
          <SectionBar title="SIGNATURES" />
          <View style={s.sigRow}>
            <View style={s.sigBox}>
              <Text style={s.sigLabel}>QA Inspector Signature</Text>
              {inspection.inspector_signature_url && inspection.inspector_signature_url.length > 0 ? (
                <Image src={inspection.inspector_signature_url} style={s.sigImg} />
              ) : (
                <View style={{ height: 44, marginBottom: 4 }} />
              )}
              <Text style={s.sigName}>{inspectorName}</Text>
              <Text style={s.sigRole}>QA Inspector</Text>
            </View>

            <View style={s.sigBox}>
              <Text style={s.sigLabel}>Factory Representative Signature</Text>
              {inspection.factory_rep_signature_url && inspection.factory_rep_signature_url.length > 0 ? (
                <Image src={inspection.factory_rep_signature_url} style={s.sigImg} />
              ) : (
                <View style={{ height: 44, marginBottom: 4 }} />
              )}
              <Text style={s.sigName}>Factory Quality Manager</Text>
              <Text style={s.sigRole}>Factory Rep</Text>
            </View>

            <View style={[s.sigBox, { flex: 0.7 }]}>
              <Text style={s.sigLabel}>Inspection Date</Text>
              <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
                <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>{formatDate(inspection.created_at)}</Text>
              </View>
              <Text style={s.sigRole}>Date of Final Audit</Text>
            </View>
          </View>
        </View>

        {/* Brand footer on last page */}
        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text
            style={{ fontSize: 7.5, color: '#555555', flex: 1, textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2563eb' }}>QAMATE</Text>
            <Text style={{ fontSize: 7.5, color: '#555555' }}>info@qamate.in | www.qamate.in</Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default FinalInspectionReportPDF;
